import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { isNil } from 'lodash-es'
import {
  AuthorisedUser,
  Country,
  CrudActions,
  EntityIdAndType,
  EntityType,
  EntityTypeEnum,
  Organisation,
  PendingTradingEntityDetail,
  SpreadsheetMapping,
  SpreadsheetMappingType,
  TradingEntity,
  WorkflowActions
} from 'api'
import { NotificationActions, NotificationType, ShowGrowlArgs } from 'store/notification'
import { CorrelatedResponseCallback, EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { Locale, Translation } from 'locale'
import { excelService, ioService } from 'services'
import { TradingEntitySpreadsheetParseResult, parseTradingEntitySpreadsheet } from './parseTradingEntitySpreadsheet'
import { cloneMapping, getDefaultMapping } from '../upload/model'
import { ModalConsumerProps, withModal } from '../modal'
import { Groupbox, ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { ButtonStrip, TextButton, TextButtonType } from 'view/components/buttons'
import { ParseResultPanel, UploadDropzoneEmpty, UploadDropzoneFull } from '../upload'
import { SpreadsheetMappingSelectboxField } from '../entity'
import { TradingEntityMappingDialog } from './TradingEntityMappingDialog'
import { PendingTradingEntityPanel } from './PendingTradingEntityPanel'
import { LoaderOverlay } from 'view/components/loader/Loader'
import * as constants from 'view/styles/constants'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createSortedSpreadsheetMappingIdsSelector } from 'selectors/spreadsheetMappingSelectors'
import { createAllEntitiesSortedSelector, createEntityGetterSelector } from 'selectors/entitySelectors'
import { organisationByShortCodeGetterSelector } from 'selectors/organisationSelectors'
import { workflowTradingEntityDetailsGetterSelector } from 'selectors/workflowSelectors'

interface TradingEntityUploadDialogProps extends ModalConsumerProps {
  // Own props
  initialFile?: File
  onClose?: () => unknown

  // State props
  authorisedUser: AuthorisedUser
  spreadsheetMappings: string[]
  getSpreadsheetMapping: (mappingId: string) => SpreadsheetMapping | null
  getOrganisationByShortCode: (shortCode: string) => Organisation | null
  getWorkflowTradingEntityDetails: (workflowIds: string[]) => PendingTradingEntityDetail[]
  allCountriesSelector: Country[]

  // Dispatch props
  onDeleteSpreadsheetMapping: (mappingId: string) => unknown
  onAddTradingEntity: (entityTypeEnum: EntityTypeEnum, correlatedResponseCallback?: CorrelatedResponseCallback) => unknown
  onUpdate: (payload: { updatedEntity: TradingEntity; workflowId: string } & EntityIdAndType) => unknown
  onBulkSubmitWorkflows: (workflowIds: string[]) => unknown
  onBulkCancelWorkflows: (workflowIds: string[]) => unknown
  onShowGrowl: (growlArgs: ShowGrowlArgs) => unknown
}

interface TradingEntityUploadDialogState {
  uploadFile?: File
  rowData: any[] | null
  parseResult: TradingEntitySpreadsheetParseResult | null
  spreadsheetMappingId: string | null
  workflowIds: string[]
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector(),
  spreadsheetMappings: createSortedSpreadsheetMappingIdsSelector(() => SpreadsheetMappingType.TRADING_ENTITY),
  getSpreadsheetMapping: createEntityGetterSelector(EntityType.SPREADSHEET_MAPPING),
  getOrganisationByShortCode: organisationByShortCodeGetterSelector,
  getWorkflowTradingEntityDetails: workflowTradingEntityDetailsGetterSelector,
  allCountriesSelector: createAllEntitiesSortedSelector(EntityType.COUNTRY)
})

const mapDispatchToProps = {
  onDeleteSpreadsheetMapping: CrudActions.spreadsheetMappingDelete,
  onAddTradingEntity: WorkflowActions.createNewWorkflow,
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onBulkSubmitWorkflows: WorkflowActions.createBulkSubmitWorkflows,
  onBulkCancelWorkflows: WorkflowActions.createBulkCancelWorkflows,
  onShowGrowl: NotificationActions.showGrowl
}

const Content = styled(ModalContent)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0;
  margin: 0 24px;
  width: 1200px;
  min-height: 500px;
  border-bottom: 1px solid ${constants.CORE_PRIMARY_1};
`

const UploadContainer = styled.section`
  flex-grow: 1;
  max-width: 450px;
`

const PendingContainer = styled.section`
  width: 720px;
  position: relative;
`

const TradingEntityUploadDialogComponent: React.FC<TradingEntityUploadDialogProps> = props => {
  const {
    initialFile,
    authorisedUser,
    spreadsheetMappings,
    getSpreadsheetMapping,
    allCountriesSelector,
    getOrganisationByShortCode,
    getWorkflowTradingEntityDetails,
    onClose,
    onDeleteSpreadsheetMapping,
    onAddTradingEntity,
    onUpdate,
    onBulkSubmitWorkflows,
    onBulkCancelWorkflows,
    onShowGrowl,
    showDialog,
    showModal
  } = props

  const [componentState, setState] = useState<TradingEntityUploadDialogState>({
    uploadFile: initialFile,
    rowData: null,
    parseResult: null,
    spreadsheetMappingId: null,
    workflowIds: []
  })

  const { uploadFile, rowData, parseResult, spreadsheetMappingId, workflowIds } = componentState

  const workflowTradingEntityDetails = getWorkflowTradingEntityDetails(workflowIds)

  const canUpload =
    workflowTradingEntityDetails.length > 0 &&
    workflowTradingEntityDetails.every(tradingEntityDetail => tradingEntityDetail.validationIssues?.length === 0)

  const countryCodes = allCountriesSelector.map(country => country.isoTwoDigitAlphaCode)

  const loadRowData = async () => {
    try {
      setState(state => ({ ...state, rowData: null, parseResult: null }))

      if (uploadFile) {
        const newFileData = await ioService.readFile(uploadFile)
        const newRowData = await excelService.parseXlsx(newFileData, { fileType: uploadFile.type })
        setState(state => ({ ...state, rowData: newRowData }))
      }
    } catch (err) {
      onShowGrowl({
        type: NotificationType.Error,
        title: Translation.TC_UPLOAD_ERROR_TITLE,
        message: Translation.SC_UPLOAD_ERROR_MESSAGE,
        messageArgs: err
      })
    }
  }

  const parseRowData = () => {
    const spreadsheetMapping = getSelectedMapping()

    if (uploadFile && rowData && spreadsheetMapping) {
      const newParseResult = parseTradingEntitySpreadsheet(
        uploadFile.name,
        rowData,
        spreadsheetMapping,
        getOrganisationByShortCode,
        countryCodes
      )
      setState(state => ({
        ...state,
        parseResult: newParseResult
      }))
    } else {
      setState(state => ({ ...state, parseResult: null }))
    }
  }

  const getSelectedMapping = () =>
    spreadsheetMappingId ? getSpreadsheetMapping(spreadsheetMappingId) : getDefaultMapping(SpreadsheetMappingType.TRADING_ENTITY)

  const ensureSelectedMapping = () => {
    const result = getSelectedMapping()

    if (isNil(result)) {
      throw new Error('Unable to load spreadsheet configuration')
    }

    return result
  }

  const validateTradingEntityParseResult = () => {
    if (parseResult && parseResult.issues.length > 0) {
      return
    }

    parseResult?.tradingEntities.forEach(tradingEntity => {
      handleAddTradingEntity(tradingEntity)
    })
  }

  const handleAddTradingEntity = (tradingEntity: TradingEntity) => {
    onAddTradingEntity(EntityTypeEnum.TRADING_ENTITY, (workflowId: string, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
      setState(state => ({
        ...state,
        workflowIds: [...state.workflowIds, workflowId]
      }))

      onUpdate({
        entityId: newlyAddedEntityIdAndTypes[EntityTypeEnum.TRADING_ENTITY],
        entityType: EntityTypeEnum.TRADING_ENTITY,
        updatedEntity: { ...tradingEntity, id: newlyAddedEntityIdAndTypes[EntityType.TRADING_ENTITY] },
        workflowId
      })
    })
  }

  const handleUpdateMapping = (mappingId: string) => {
    if (workflowIds.length > 0) {
      onBulkCancelWorkflows(workflowIds)
    }
    setState(state => ({ ...state, spreadsheetMappingId: mappingId, workflowIds: [] }))
  }

  const handleDropFiles = (files: File[]) => {
    if (workflowIds.length > 0) {
      onBulkCancelWorkflows(workflowIds)
    }
    setState(state => ({ ...state, uploadFile: files[0], workflowIds: [] }))
  }

  const handleNewMapping = () => {
    showMappingDialog(cloneMapping(ensureSelectedMapping(), authorisedUser.id), Locale.translate(Translation.TC_CREATE_MAPPING))
  }

  const handleEditMapping = () => {
    showMappingDialog(ensureSelectedMapping(), Locale.translate(Translation.TC_UPDATE_MAPPING))
  }

  const handleDeleteMapping = async () => {
    const mapping = ensureSelectedMapping()

    if (mapping.id === null) {
      throw new Error('No spreadsheet mapping is selected')
    }

    const result = await showDialog({
      type: NotificationType.Confirm,
      title: Translation.TC_PROMPT_TITLE_DELETE_SPREADSHEET_MAPPING,
      message: Translation.SC_PROMPT_MSG_DELETE_SPREADSHEET_MAPPING,
      messageArgs: { name: mapping.name },
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO
    })

    if (result === Translation.TC_BUTTON_YES) {
      onDeleteSpreadsheetMapping(mapping.id)
      setState({ ...componentState, spreadsheetMappingId: null })
    }
  }

  const showMappingDialog = async (mapping: SpreadsheetMapping, title: string) => {
    if (!parseResult) {
      throw new Error('Spreadsheet has not been loaded')
    }

    const mappingId = await showModal(
      <TradingEntityMappingDialog
        mapping={mapping}
        title={title}
        parseResult={parseResult}
        getOrganisationByShortCode={getOrganisationByShortCode}
      />
    )

    if (mappingId) {
      if (workflowIds.length > 0) {
        onBulkCancelWorkflows(workflowIds)
      }
      setState({ ...componentState, spreadsheetMappingId: mappingId, workflowIds: [] })
    }
  }

  const handleUpload = () => {
    onBulkSubmitWorkflows(workflowIds)
    if (onClose) {
      onClose()
    }
  }

  const handleCancel = () => {
    if (workflowIds.length > 0) {
      onBulkCancelWorkflows(workflowIds)
    }
    if (onClose) {
      onClose()
    }
  }

  useEffect(() => {
    if (spreadsheetMappings.length === 1) {
      setState(state => ({ ...state, spreadsheetMappingId: spreadsheetMappings[0] }))
    }
    loadRowData()
  }, [])

  useEffect(() => {
    loadRowData()
  }, [uploadFile])

  useEffect(() => {
    parseRowData()
  }, [rowData, spreadsheetMappingId, getSpreadsheetMapping])

  useEffect(() => {
    validateTradingEntityParseResult()
  }, [parseResult])

  return (
    <ModalWindow title={Locale.translate(Translation.TC_UPLOAD_TRADING_ENTITY)}>
      <Content>
        <UploadContainer>
          <Groupbox title={Locale.translate(Translation.TC_UPLOAD_FILE)}>
            {uploadFile ? (
              <UploadDropzoneFull uploadFile={uploadFile} handleDropFiles={handleDropFiles} dataTestId="SpreadsheetParseDropzone" />
            ) : (
              <UploadDropzoneEmpty
                narrative={Locale.translate(Translation.SC_TRADING_ENTITY_DROPZONE_NARRATIVE)}
                handleDropFiles={handleDropFiles}
                dataTestId="SpreadsheetParseDropzone"
              />
            )}
          </Groupbox>
          {uploadFile ? (
            <Groupbox title={Locale.translate(Translation.TC_COLUMN_MAPPINGS)}>
              <ParseResultPanel
                parseResult={parseResult}
                itemField="tradingEntities"
                successMessage={Translation.SC_TRADING_ENTITY_SPREADSHEET_SUCCESS}
                errorMessage={Translation.SC_TRADING_ENTITY_SPREADSHEET_ERROR}
                errorNarrative={Translation.SC_UPLOAD_ERROR_NARRATIVE}
              />
              <SpreadsheetMappingSelectboxField
                label={Locale.translate(Translation.SC_SAVED_MAPPINGS)}
                placeholder={Locale.translate(Translation.SC_SYSTEM_MAPPING)}
                value={spreadsheetMappingId}
                canClear={true}
                onUpdate={handleUpdateMapping}
                mappingType={SpreadsheetMappingType.TRADING_ENTITY}
              />
              <ButtonStrip horizontalLayout={constants.HorizontalLayout.Right} spacing="3px">
                <TextButton type={TextButtonType.Bad} disabled={isNil(spreadsheetMappingId)} onClick={handleDeleteMapping}>
                  {Locale.translate(Translation.TC_BUTTON_DELETE)}
                </TextButton>
                <TextButton type={TextButtonType.Secondary} disabled={isNil(spreadsheetMappingId)} onClick={handleEditMapping}>
                  {Locale.translate(Translation.TC_BUTTON_UPDATE)}
                </TextButton>
                <TextButton type={TextButtonType.Secondary} onClick={handleNewMapping}>
                  {Locale.translate(Translation.TC_BUTTON_CREATE)}
                </TextButton>
              </ButtonStrip>
            </Groupbox>
          ) : null}
        </UploadContainer>
        <PendingContainer>
          {parseResult &&
            parseResult.issues.length === 0 &&
            (workflowTradingEntityDetails.length === parseResult.rows.length ? (
              <PendingTradingEntityPanel tradingEntityList={workflowTradingEntityDetails} />
            ) : (
              <LoaderOverlay />
            ))}
        </PendingContainer>
      </Content>
      <ModalFooter>
        <TextButton type={TextButtonType.Tertiary} onClick={handleCancel}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton type={TextButtonType.Primary} disabled={!canUpload} onClick={handleUpload}>
          {Locale.translate(Translation.TC_BUTTON_UPLOAD)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const TradingEntityUploadDialog = withModal(connect(mapStateToProps, mapDispatchToProps)(TradingEntityUploadDialogComponent))
